import { fromPairs, sortBy, split } from 'lodash-es';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { inject } from '@angular/core';
import { UserConfigurationSettingsService } from '../services/settings/user-configuration-settings.service';
import { UserMenuItem } from 'estensa-module';
import { Logger } from '@se/se.platform.base';
import { BasePatient, ICommonWorklistExaminationDTO } from '@se/models';
import { HasLockInfo } from '../services/examination-utilities.service';

/**
 * Ritrova i valori dei parametri nella url corrente.
 */
export function getQueryParams() {
    const queryParamsArr = window.location.search
        // Rimuovi '?' iniziale
        .substring(1)
        .split('&')
        .map((qp) => split(qp, '=', 2));

    return fromPairs(queryParamsArr);
}

function isTimingSupported() {
    return 'performance' in window;
}

export function logTime(fn: () => unknown, prefix?: string, logger?: Logger): unknown {
    const timeNow = isTimingSupported() ? () => window['performance'].now() : () => new Date().getTime();

    const startTime = timeNow();
    const returnValue: unknown = fn();
    const endTime = timeNow();

    let strPrefix = '';
    if (!!prefix) {
        strPrefix = `${prefix}: `;
    }

    const msg = `${strPrefix}${endTime - startTime}ms`;
    if (logger) {
        logger.trace(msg);
    } else {
        console.debug(msg);
    }

    return returnValue;
}

export function orderCollectionByStringProp(
    collection: any,
    property: any,
    trim: boolean = true,
    lowercase: boolean = true,
) {
    const orderedCollection = sortBy(collection, (data) => {
        const splittedProp = data[property].split(' ');
        let comparerString = '';
        // Uso come comparatore la prima stringa non vuota, trimmata e lowercase
        for (let sp of splittedProp) {
            if (!!sp.trim()) {
                comparerString = sp;
                if (lowercase) {
                    comparerString = comparerString.toLowerCase();
                }
                if (trim) {
                    comparerString = comparerString.trim();
                }

                return comparerString;
            }
        }

        return data[property];
    });

    return orderedCollection;
}

export function arrayBufferToBase64(arrayBuffer: ArrayBuffer) {
    let binary = '';
    const bytes = new Uint8Array(arrayBuffer);
    const len = bytes.byteLength;
    if (len > 0) {
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }

    return null;
}

export function convertBase64ToUint8Array(base64: string): Uint8Array {
    const raw = window.atob(base64);
    const rawLength = raw.length;
    const array = new Uint8Array(new ArrayBuffer(rawLength));

    for (let i = 0; i < rawLength; i++) {
        array[i] = raw.charCodeAt(i);
    }

    return array;
}

export function jsonBlob(obj: any) {
    return new Blob([JSON.stringify(obj)], { type: 'application/json' });
}

export function getBrowserTabTitle(defaultKey: string, routeMainStateCode: string): string {
    let translateService = inject(TranslateService);
    let userConfigurationSettingsService = inject(UserConfigurationSettingsService);

    let translationKey: string = defaultKey;
    try {
        let menuItem: UserMenuItem | undefined = userConfigurationSettingsService.userConfiguration?.menu.items.filter(
            (x) => x.identifier == routeMainStateCode,
        )?.[0];

        if (menuItem?.customDescription) translationKey = menuItem.customDescription;
    } catch {
        translationKey = defaultKey;
    }

    return translateService.instant(translationKey);
}

export const titleResolver: ResolveFn<string> = (route: ActivatedRouteSnapshot) => {
    let path: string = route.routeConfig?.path ?? '';

    if (path) {
        switch (path) {
            case 'cardio-visit':
                return getBrowserTabTitle('TitleCardioVisit', route.data.mainStateCode);
            case 'ecg':
                return getBrowserTabTitle('TitleEcgReportWorklist', route.data.mainStateCode);
            case 'execution':
                return getBrowserTabTitle('CategoryExecution', route.data.mainStateCode);
            case 'specialty-reporting':
                return getBrowserTabTitle('TitleSpecialtyWorklist', route.data.mainStateCode);
            case 'onprintingcd':
                return getBrowserTabTitle('TitleWorklistOnPrintingCD', route.data.mainStateCode);
            case 'completedcd':
                return getBrowserTabTitle('TitleWorklistCompletedCD', route.data.mainStateCode);
            case 'default-text':
                return getBrowserTabTitle('TitleDefaultTextMgnt', route.data.mainStateCode);
            case '**':
                return 'Suitestensa';
        }
    }

    return '';
};

/*alcune worklist ritornano liste di esami il cui patient ha "name e surname" altre "firstName e lastName"*/
export const getSePatientName = (patient: BasePatient) => patient.name || patient.firstName || '';
export const getSePatientSurname = (patient: BasePatient) => patient.surname || patient.lastName || '';
